import React from "react";
import { object, shape, string, any, arrayOf, array } from "prop-types";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { BRAND_NAMES } from "@insightfulscience/contentful/constants";
import { useLocale } from "@insightfulscience/smart-react/i18";
import Hero from "@insightfulscience/atomic-react/Hero";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Image from "@insightfulscience/atomic-react/Image";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import Section from "@insightfulscience/atomic-react/Section";
import Line from "@insightfulscience/atomic-react/Line";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import GuidePageTemplateSidebar from "@insightfulscience/atomic-react/GuidePageTemplate/Sidebar";
import {
	GuidePageTemplateNavItem,
	GuidePageTemplateNavItemLink,
} from "@insightfulscience/atomic-react/GuidePageTemplate/Nav";

import Link from "@insightfulscience/atomic-react/Link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
	generateHeadingId,
	sidebarNavigation,
} from "@insightfulscience/contentful/richTextTypes/default/utils";
import richTextTypes from "@insightfulscience/contentful/richTextTypes/default";
import renderBlockByType from "@insightfulscience/contentful/utils/renderBlockByType";
import Heading from "@insightfulscience/atomic-react/Heading";
import RecommendedVideos from "@insightfulscience/contentful/ui/RecommendedVideos";
import Block from "@insightfulscience/atomic-react/Block";
import Button from "@insightfulscience/atomic-react/Button";
import Inline from "@insightfulscience/atomic-react/Inline";
import Icon from "@insightfulscience/atomic-react/Icon";
import { scrollToHeading } from "@insightfulscience/atomic-react/utils/Guides";
import routes from "../../../routes";

const logoAcademy = "/images/geneious/logo/logo-geneious-academy.svg";
const OPEN_SOURCE = "Open Source";
const EXPERIMENTAL = "Experimental";

const PluginPage = ({ post, pageProps }) => {
	const [t] = useLocale("plugins");
	const { siteDomain, brandCode } = pageProps;
	const {
		title,
		subtitle,
		metaDescription = "",
		metaTitle = "",
		slug,
		pluginLink,
		body,
		tag = [],
		thumbnail,
		additionalResources = [],
		recommendedVideos = [],
		academy = false,
	} = post;

	const thumbnailUrl = thumbnail?.fields?.file?.url || "";
	const navigation = sidebarNavigation(body);
	const totalHeading2 = body.content.reduce((total, current) => {
		return total + (current.nodeType === "heading-2" ? 1 : 0);
	}, 0);
	const isSidebar = totalHeading2 > 0;
	const brandName = BRAND_NAMES[brandCode];

	return (
		<>
			<PageMeta title={metaTitle} description={metaDescription}>
				<PageMeta.OGP
					title={metaTitle ? `${title} - ${brandName}` : null}
					description={metaDescription || null}
					url={`${siteDomain}/plugins/${slug}`}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={metaTitle ? `${title} - ${brandName}` : null}
					description={metaDescription || null}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Canonical href={`${siteDomain}/plugins/${slug}`} />
			</PageMeta>
			<Hero color="white" spacing={{ pb: 10 }}>
				{academy && (
					<Row utility={{ justify: "center" }} spacing={{ mb: 6 }}>
						<Col lg={5} md={5} sm={5}>
							<Link href={routes.external.resources}>
								<Image src={logoAcademy} isBaseUrl />
							</Link>
						</Col>
					</Row>
				)}
				<HeroTitle weight="medium" color="black">
					{title}
				</HeroTitle>
				{subtitle && <HeroDescription>{subtitle}</HeroDescription>}
			</Hero>
			<GuidePageTemplate>
				{isSidebar && (
					<GuidePageTemplateSidebar spacing={{ mb: 6 }}>
						{pluginLink && (
							<GuidePageTemplateNavItem>
								<Block sx={{ textAlign: "center", md: { textAlign: "left" } }}>
									<Button.Link href={pluginLink} size="s" isPreLine>
										Download {title} Plugin
									</Button.Link>
								</Block>
							</GuidePageTemplateNavItem>
						)}
						<GuidePageTemplateNavItem>
							<Block
								spacing={{ pt: 4, pb: 6 }}
								utility={{ border: { bottom: { width: 1, color: "dotmatics-1" } } }}
							>
								{post.requiredVersion && (
									<Inline sx={{ fontColor: "black--custom-1" }}>{post.requiredVersion}</Inline>
								)}
							</Block>
						</GuidePageTemplateNavItem>
						{navigation.map(item => {
							return (
								<GuidePageTemplateNavItem key={item}>
									<GuidePageTemplateNavItemLink
										href={`#${generateHeadingId(item)}`}
										onClick={e => scrollToHeading(e)}
									>
										{item}
									</GuidePageTemplateNavItemLink>
								</GuidePageTemplateNavItem>
							);
						})}

						<GuidePageTemplateNavItem>
							<Line color="dotmatics-1" />
						</GuidePageTemplateNavItem>
						<GuidePageTemplateNavItem>
							{tag.includes(OPEN_SOURCE) && (
								<Block spacing={{ mb: 4 }}>
									<Link href="/" sx={{ fontColor: "black" }}>
										<Icon name="mac" spacing={{ mr: 4 }} size="l" />
										Open Source
									</Link>
								</Block>
							)}
							{tag.includes(EXPERIMENTAL) && (
								<Block>
									<Link href="/" sx={{ fontColor: "black" }}>
										<Icon name="windows" spacing={{ mr: 4 }} size="l" />
										Experimental
									</Link>
								</Block>
							)}
						</GuidePageTemplateNavItem>
					</GuidePageTemplateSidebar>
				)}
				<GuidePageTemplateMain>
					{documentToReactComponents(body, richTextTypes)}
				</GuidePageTemplateMain>
			</GuidePageTemplate>
			{recommendedVideos.length > 0 && (
				<Section spacing={{ py: 16 }}>
					<Heading Tag="h2" sx={{ fontSize: "l", textAlign: "center" }}>
						Recommended Resources
					</Heading>
					<Container>
						<RecommendedVideos brandCode={brandCode} items={recommendedVideos} />
					</Container>
				</Section>
			)}
			{additionalResources.map(renderBlockByType(slug))}
		</>
	);
};

PluginPage.propTypes = {
	post: shape({
		title: string.isRequired,
		subtitle: string,
		tag: array, // eslint-disable-line react/forbid-prop-types,
		metaDescription: string,
		slug: string.isRequired,
		lead: string,
		body: object, // eslint-disable-line react/forbid-prop-types,
		toCLink: string,
		toCDownloadEbookLink: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string.isRequired,
		brandCode: string,
		breadcrumbs: arrayOf(
			shape({
				title: string.isRequired,
				href: string.isRequired,
			}),
		),
	}),
};

export default PluginPage;
